/**
 * Plain-word support badges. The words carry the status; colour only repeats it.
 *   "Supported until Nov 14, 2028" / "Ends in 120 days (Mar 3, 2027)" / "Out of support (ended Dec 31, 2025)"
 */

import type { EolRow } from './types';
import { dayNumber } from './eol';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "2028-11-14" -> "Nov 14, 2028". Locale-independent so every screen reads the same. */
export function formatDate(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso;
  return `${MONTHS[Number(m[2]) - 1]} ${Number(m[3])}, ${m[1]}`;
}

/** "2026-10" -> "Oct 2026". */
export function formatMonth(yyyyMm: string): string {
  const m = /^(\d{4})-(\d{2})$/.exec(yyyyMm);
  if (!m) return yyyyMm;
  return `${MONTHS[Number(m[2]) - 1]} ${m[1]}`;
}

export function badgeText(row: Pick<EolRow, 'status' | 'supportEnds' | 'supportEndsMonth' | 'daysRemaining'>): string {
  const when = row.supportEnds ? formatDate(row.supportEnds) : row.supportEndsMonth ? formatMonth(row.supportEndsMonth) : null;
  switch (row.status) {
    case 'supported':
      return when ? `Supported until ${when}` : 'Supported (no end date published)';
    case 'ending-soon': {
      const d = row.daysRemaining ?? null;
      if (d === 0) return `Support ends today${when ? ` (${when})` : ''}`;
      if (d !== null) return `Ends in ${d} ${d === 1 ? 'day' : 'days'}${when ? ` (${when})` : ''}`;
      return when ? `Ends ${when}` : 'Ending soon';
    }
    case 'unsupported':
      return when ? `Out of support (ended ${when})` : 'Out of support (no end date published)';
    default:
      return 'No support date published';
  }
}

/** "reviewed Nov 3, 2026 (12 days ago)" */
export function catalogFreshness(lastReviewed: string, today: string): string {
  const a = dayNumber(lastReviewed);
  const b = dayNumber(today);
  if (a === null || b === null) return `reviewed ${lastReviewed}`;
  const ago = b - a;
  const rel = ago <= 0 ? 'today' : ago === 1 ? '1 day ago' : `${ago} days ago`;
  return `reviewed ${formatDate(lastReviewed)} (${rel})`;
}
